import { useMemo, useCallback } from 'react';
import { prop, pick } from 'ramda';
import { useUserSelector, useDeviceSelector, useAppSelector } from 'js/store/hooks';

import * as helpers from '../helpers';
import * as constants from '../constants';

export const useDateFormatter = () => {
    const userLocale = useUserSelector(prop('lang'));
    const { os, formFactor } = useDeviceSelector(pick(['os', 'formFactor']));
    const isLocaleDateStringSupported = !(os === 'ios' && formFactor !== 'desktop');
    const { dateFormat, timeFormat } = useAppSelector(pick(['dateFormat', 'timeFormat']));

    const getDateFormat = useCallback((type) => {
        const replacedDateFormat = dateFormat
            .replace('MM', '%m')
            .replace('DD', '%d')
            .replace('YYYY', '%Y');
        const dateType = constants.DATE_FORMATS[type];
        const parsedDateFormat = dateType.replace(/%Y(.*)%d/, replacedDateFormat);

        if (timeFormat === '12-hour' && parsedDateFormat.includes('%H')) {
            return parsedDateFormat.replace('%H', '%I').concat(' %p');
        }

        return parsedDateFormat;
    }, [dateFormat, timeFormat]);

    const formatDate = useCallback((srcDate = new Date(), format, locale = userLocale) =>
        helpers.formatDate({
            format,
            locale,
            date: helpers.parseDate(srcDate)
        }), [userLocale]
    );

    const getDate = useCallback((srcDate, locale) => formatDate(srcDate, getDateFormat('date'), locale),
        [formatDate, getDateFormat]);

    const date = useCallback((srcDate, customFormat, locale) => {
        return customFormat
            ? formatDate(srcDate, customFormat, locale)
            : getDate(srcDate, locale);
    }, [formatDate, getDate]);

    const getLocaleDateShort = useCallback((srcDate = new Date(), locale = userLocale) => {
        // for long string locales
        const year = locale === 'ru-RU' ? '2-digit' : 'numeric';

        return !isLocaleDateStringSupported
            ? getDate(srcDate, locale) // Safari doesn't support locale date string
            : new Date(srcDate).toLocaleDateString(locale.replace(/_/g, '-'), {
                day: 'numeric',
                month: 'short',
                year
            });
    }, [getDate, userLocale, isLocaleDateStringSupported]);

    return useMemo(() => ({
        date,
        getDate,
        getLocaleDateShort,
        parseImsDate: helpers.parseImsDate,
        getDateForIms: helpers.getDateForIms,
        parseImsBankIdDate: helpers.parseImsBankIdDate,
        getDateTimeLong: (srcDate, locale) => formatDate(srcDate, getDateFormat('dateTimeLong'), locale),
        getDateTimeLongMs: (srcDate, locale) => formatDate(srcDate, getDateFormat('dateTimeLongMs'), locale),
        getDateTimeShort: (srcDate, locale) => formatDate(srcDate, getDateFormat('dateTimeShort'), locale),
        getTimeShort: (srcDate, locale) => formatDate(srcDate, getDateFormat('timeShort'), locale),
        getTimeLong: (srcDate, locale) => formatDate(srcDate, getDateFormat('timeLong'), locale),
        getDateForCsv: (srcDate, locale) => formatDate(srcDate, '%Y-%m-%d', locale),
        getDateTimeForCsv: (srcDate, locale) => formatDate(srcDate, '%Y-%m-%d %H:%M', locale)
    }), [
        date,
        getDate,
        formatDate,
        getDateFormat,
        getLocaleDateShort
    ]);
};

